import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { errorCode } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { getCompanyZone, zoneLabel, zoneName } from "../lib/time";
import {
  defaultEnd, localInputToIso, missionErrorMessage, periodError, typeChoices, warningKinds, warningsFrom,
} from "../lib/missions";
import { MemberKindHint, MemberPicker, WarningsNotice, levelLabel } from "../components/missions/MissionBits";
import { useMissionTypes } from "../components/missions/MissionTypesPanel";
import { ProspectFilter } from "../components/TaskFormSheet";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import useDraft from "../hooks/useDraft";
import DraftBanner from "../components/DraftBanner";
import { toast } from "sonner";
import { AlertCircle, ArrowLeft, Info, Loader2, X } from "lucide-react";

const EMPTY = { prospect: null, pilotId: null, typeId: "", title: "", objective: "", start: "", end: "", lead: null };

// Nouvelle mission. A mission is created as a draft; it enters the team's agenda when it is planned, from its page.
export default function MissionFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [missionZone] = useState(getCompanyZone); // the zone of the place of work: the company's, fixed for this mission
  const { types, status: typesStatus, reload: reloadTypes } = useMissionTypes();
  const [draft, setDraft] = useState(EMPTY);
  const [endTouched, setEndTouched] = useState(false);
  const [titleTouched, setTitleTouched] = useState(false);
  const [warnings, setWarnings] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const idempotencyKey = useRef(crypto.randomUUID());
  const canPickPharmacy = hasPermission(user, "prospects:view");
  const [searchParams] = useSearchParams();
  const fromProspect = searchParams.get("prospect");
  const fromPilot = searchParams.get("pilot");
  const fromTicket = searchParams.get("ticket"); // « Demander une intervention » from a support ticket: linked once created

  // Opened from a prospect page or a pilot workspace (?prospect=…&pilot=…): the pharmacy is filled in, the pilot linked.
  useEffect(() => {
    if (!fromProspect || !canPickPharmacy) return;
    api.get(`/prospects/${fromProspect}`)
      .then(({ data }) => setDraft((d) => (d.prospect ? d : { ...d, prospect: { id: data.id, pharmacy_name: data.pharmacy_name }, pilotId: fromPilot || null })))
      .catch(() => toast.error("Cette pharmacie n'est pas accessible : choisissez-la dans la liste."));
  }, [fromProspect, fromPilot, canPickPharmacy]);

  // Any change of the content is a new request: a new key (a refused attempt left nothing behind on the server).
  useEffect(() => { idempotencyKey.current = crypto.randomUUID(); setWarnings(null); setConfirmed(false); }, [draft]);

  const type = types.find((t) => t.id === draft.typeId) || null;
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  // A mission opened from a pharmacy, pilot or ticket is already filled in: only a blank form offers the draft kept on this device.
  const local = useDraft({
    name: "mission-new", userId: user?.id, values: draft, enabled: !fromProspect && !fromPilot && !fromTicket,
    apply: (saved) => { setDraft({ ...EMPTY, ...saved }); setTitleTouched(!!saved.title); setEndTouched(!!saved.end); },
  });

  const onType = (typeId) => {
    const next = types.find((t) => t.id === typeId);
    const patch = { typeId };
    if (!titleTouched && next) patch.title = draft.prospect ? `${next.label} · ${draft.prospect.pharmacy_name}` : next.label;
    if (!endTouched && draft.start) patch.end = defaultEnd(draft.start, next?.default_duration_minutes, missionZone);
    set(patch);
  };
  const onPharmacy = (prospect) => {
    const patch = { prospect, pilotId: prospect && draft.prospect && prospect.id === draft.prospect.id ? draft.pilotId : null };
    if (!titleTouched && type && prospect) patch.title = `${type.label} · ${prospect.pharmacy_name}`;
    set(patch);
  };
  const onStart = (start) => {
    const patch = { start };
    if (!endTouched) patch.end = defaultEnd(start, type?.default_duration_minutes, missionZone);
    set(patch);
  };

  const startIso = localInputToIso(draft.start, missionZone);
  const endIso = localInputToIso(draft.end, missionZone);
  const datesError = draft.start || draft.end ? periodError(startIso, endIso) : "";
  const valid = !!draft.prospect && !!draft.typeId && draft.title.trim().length > 0 && !!startIso && !!endIso && !datesError
    && (!warnings || confirmed);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    const body = {
      type_id: draft.typeId, prospect_id: draft.prospect.id, title: draft.title.trim(), objective: draft.objective.trim() || null,
      timezone: missionZone, planned_start_at: startIso, planned_end_at: endIso,
      ...(draft.pilotId ? { pilot_id: draft.pilotId } : {}),
      ...(draft.lead ? { lead: { kind: draft.lead.kind, id: draft.lead.id } } : {}),
      ...(warnings && confirmed ? { confirm_warnings: warningKinds(warnings) } : {}),
    };
    try {
      const { data } = await api.post("/missions", body, { headers: { "Idempotency-Key": idempotencyKey.current } });
      if (fromTicket) {
        try {
          await api.post(`/tickets/${fromTicket}/links`, { kind: "mission", ref: data.id, escalation: true });
          toast.success(`Mission ${data.reference} créée et reliée au ticket`);
        } catch {
          toast.warning(`Mission ${data.reference} créée, mais pas reliée au ticket : reliez-la depuis le ticket.`);
        }
      } else {
        toast.success(`Mission ${data.reference} créée`);
      }
      local.clear();
      navigate(`/missions/${data.id}`, { replace: true });
    } catch (err) {
      const found = warningsFrom(err);
      if (found) {
        setWarnings(found);
        setConfirmed(false);
      } else {
        setError(missionErrorMessage(err, "Impossible de créer la mission."));
        if (errorCode(err) === "type_inactive") reloadTypes();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="mission-form-page">
      <Link to="/missions" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Retour aux missions</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Nouvelle mission</h1>
        <p className="text-sm text-slate-500">Préparez l'intervention. Elle reste en brouillon jusqu'à ce que vous la planifiiez.</p>
      </div>

      <DraftBanner pending={local.pending} onRestore={local.restore} onDismiss={local.dismiss} />

      {!canPickPharmacy && (
        <Card className="p-4 flex gap-3 border-amber-200 bg-amber-50" data-testid="mission-form-no-pharmacy">
          <AlertCircle size={18} className="text-amber-700 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">Pour créer une mission, il faut pouvoir consulter les pharmacies (prospects). Demandez cet accès à un administrateur.</p>
        </Card>
      )}

      <form onSubmit={submit} className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <h3 className="text-base font-semibold text-slate-900">Où et quoi</h3>
            <div className="space-y-2">
              <Label>Pharmacie *</Label>
              {canPickPharmacy ? <ProspectFilter value={draft.prospect} onChange={onPharmacy} /> : <Input disabled placeholder="Accès aux prospects nécessaire" />}
              {draft.pilotId && (
                <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="mission-form-pilot">
                  <span>Rattachée au pilote de cette pharmacie.</span>
                  <button type="button" className="underline hover:text-slate-900" onClick={() => set({ pilotId: null })}>Ne pas rattacher</button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="mission-type">Type de mission *</Label>
              {typesStatus === "loading" ? <Skeleton className="h-10 w-full" /> : typeChoices(types).length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="mission-form-no-type">
                  Aucun type de mission actif. {hasPermission(user, "missions:manage_types") ? <Link className="underline" to="/collaborateurs/organisation?tab=mission_types">Créez-en un</Link> : "Demandez à un administrateur d'en créer un."}
                </p>
              ) : (
                <Select value={draft.typeId || undefined} onValueChange={onType}>
                  <SelectTrigger id="mission-type" data-testid="mission-form-type"><SelectValue placeholder="Choisir un type" /></SelectTrigger>
                  <SelectContent>{typeChoices(types).map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="mission-title">Titre *</Label>
              <Input id="mission-title" maxLength={120} className="text-base md:text-sm" value={draft.title}
                onChange={(e) => { setTitleTouched(true); set({ title: e.target.value }); }} data-testid="mission-form-title" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mission-objective">Objectif</Label>
              <Textarea id="mission-objective" rows={3} maxLength={2000} className="text-base md:text-sm" placeholder="Ce que la mission doit permettre d'obtenir."
                value={draft.objective} onChange={(e) => set({ objective: e.target.value })} data-testid="mission-form-objective" />
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Quand</h3>
              <p className="text-xs text-slate-500">Heure de {zoneLabel(missionZone)}, le fuseau du lieu de travail.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="mission-start">Début prévu * <span className="font-normal text-slate-500" data-testid="mission-zone-hint">(heure de {zoneName(missionZone)})</span></Label>
                <Input id="mission-start" type="datetime-local" className="text-base md:text-sm" value={draft.start} onChange={(e) => onStart(e.target.value)} data-testid="mission-form-start" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mission-end">Fin prévue *</Label>
                <Input id="mission-end" type="datetime-local" className="text-base md:text-sm" value={draft.end}
                  onChange={(e) => { setEndTouched(true); set({ end: e.target.value }); }} data-testid="mission-form-end" />
              </div>
            </div>
            {datesError && <p className="text-sm text-amber-700" data-testid="mission-form-dates-error">{datesError}</p>}
          </Card>

          <Card className="p-5 space-y-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Responsable de la mission</h3>
              <p className="text-xs text-slate-500">Il coordonne l'intervention sur place. Vous pourrez compléter l'équipe depuis la page de la mission.</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0"><MemberPicker value={draft.lead} onChange={(lead) => set({ lead })} testid="mission-form-lead" placeholder="Choisir le responsable (facultatif)" /></div>
              {draft.lead && <Button type="button" variant="ghost" size="icon" aria-label="Retirer le responsable" onClick={() => set({ lead: null })}><X size={15} /></Button>}
            </div>
            {draft.lead && <MemberKindHint kind={draft.lead.kind} />}
          </Card>

          <WarningsNotice warnings={warnings} confirmed={confirmed} onConfirmedChange={setConfirmed} />
          {error && <p role="alert" className="text-sm text-red-700" data-testid="mission-form-error">{error}</p>}

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/missions")} disabled={saving}>Annuler</Button>
            <Button type="submit" disabled={!valid || saving || !canPickPharmacy} data-testid="mission-form-submit">
              {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}{warnings && confirmed ? "Confirmer et créer" : "Créer la mission"}
            </Button>
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24">
          <Card className="p-5 space-y-3" data-testid="mission-form-type-summary">
            <h3 className="text-sm font-semibold text-slate-900">Compétences attendues</h3>
            {!type ? <p className="text-sm text-slate-500">Choisissez un type pour voir ce qu'il demande.</p>
              : type.required_skills.length === 0 ? <p className="text-sm text-slate-500">Ce type ne demande aucune compétence particulière.</p>
                : (
                  <ul className="space-y-1 text-sm text-slate-700">
                    {type.required_skills.map((s) => <li key={s.skill_id}>{s.label} <span className="text-slate-400">· {levelLabel(s.min_level)} minimum</span></li>)}
                  </ul>
                )}
          </Card>
          <Card className="p-5 space-y-2 bg-slate-50/60">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Info size={15} className="text-slate-400" /> Bon à savoir</div>
            <ul className="space-y-1.5 text-xs text-slate-600 list-disc pl-4">
              <li>Une personne sans la compétence attendue, ou déjà prise ailleurs, est signalée : vous pouvez confirmer quand même.</li>
              <li>Les membres de l'équipe qui ont un compte voient la mission et les coordonnées de la pharmacie, rien de plus.</li>
              <li>Un collaborateur sans compte peut être affecté, mais n'a aucun accès au CRM.</li>
            </ul>
          </Card>
        </aside>
      </form>
    </div>
  );
}
